import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getAnalyticsService } from 'services/analyticsService';

import { AppContext } from '../../../context/app';
import useIsMobile from '../../../hooks/useIsMobile';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useShareToEmail } from '../../../hooks/useShareToEmail';
import ShareDirectionsToMobileModal from '../../shareDirectionsToMobileModal/ShareDirectionsToMobileModal';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import ModalSendEmailDirections from '../modalSendEmail/ModalSendEmailDirections';
import ShareButton from '../shareButton/ShareButton';
import TextButton from '../textButton/TextButton';
import styles from './shareBar.module.scss';

type ShareBarProps = {
  onClose?: () => void;
  topSeparator?: boolean;
  showDriveTime?: boolean;
};

export const ShareBar: React.FC<ShareBarProps> = ({
  onClose,
  topSeparator,
  showDriveTime,
}: ShareBarProps) => {
  const analyticsService = useMemo(() => getAnalyticsService(), []);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('common');
  const backButtonLabel = t('campusNavigation.share.backButton');
  const isOnOverviewPage = useMemo(() => !onClose, [onClose]);
  const { directions, setShowPrintDirectionView, navDestinationPlace } =
    useContext(AppContext);

  const { kioskMode } = useKioskManager();

  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [
    isShareDirectionsToPhoneModalOpen,
    setIsShareDirectionsToPhoneModalOpen,
  ] = useState<boolean>(false);

  const isMobile = useIsMobile();
  const shareToEmail = useShareToEmail();

  const handleShareEmail = (
    email: string,
    destinationName: string,
    queryParams: { [key: string]: any },
  ) => {
    shareToEmail([email], destinationName, queryParams);
  };

  const handleShareMobile = () => {
    analyticsService.track(analyticsService.events.ShareToPhone);
    setIsShareDirectionsToPhoneModalOpen(true);
  };

  const driveTime = useMemo(
    () => directions?.travelTime.description || null,
    [directions],
  );

  const isWeb = useMemo(() => !isMobile && !kioskMode, [isMobile, kioskMode]);

  return (
    <>
      <ModalSendEmailDirections
        onSubmit={(email) => {
          const queryParams: { [key: string]: any } = {};
          Array.from(searchParams.entries()).forEach(([key, value]) => {
            queryParams[key] = value;
          });
          const destinationName = String(navDestinationPlace?.name);
          handleShareEmail(email, destinationName, queryParams);
        }}
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />
      <ShareDirectionsToMobileModal
        isOpen={isShareDirectionsToPhoneModalOpen}
        onClose={() => setIsShareDirectionsToPhoneModalOpen(false)}
      />
      {topSeparator && <hr />}
      <div className={styles.bar}>
        {onClose && (
          <TextButton
            label={backButtonLabel}
            onClick={onClose}
            underline
            capitalize
          />
        )}
        {showDriveTime ? (
          <div className={styles.driveTime}>
            <p className={styles.driveTimeTitle}>
              {t('campusNavigation.share.driveTime')}
            </p>
            <Icon icon={IconType.CAR} color="#454E58" size={24} />
            <span>{driveTime}</span>
          </div>
        ) : (
          <>
            {isOnOverviewPage && (
              <div className={styles.driveTime}>
                <Icon icon={IconType.WALKING} color="#454E58" size={24} />
                <p className={styles.driveTimeTitle}>
                  {t('campusNavigation.share.walk')}
                </p>
              </div>
            )}
          </>
        )}
        <div className={styles.buttons}>
          {isWeb && (
            <>
              <ShareButton
                ariaLabel="Send to mobile"
                icon={IconType.SEND_MOBILE}
                onClick={handleShareMobile}
              />
              <ShareButton
                ariaLabel="Print"
                icon={IconType.PRINT}
                onClick={() => {
                  analyticsService.track(analyticsService.events.ShareToPrint);
                  setShowPrintDirectionView(true);
                }}
              />
              <ShareButton
                ariaLabel="Send to email"
                icon={IconType.SEND_EMAIL}
                onClick={() => setIsEmailModalOpen(true)}
              />
            </>
          )}
        </div>
        {isMobile && isOnOverviewPage && (
          <ShareButton
            icon={IconType.SEND_EMAIL}
            onClick={() => setIsEmailModalOpen(true)}
          />
        )}
      </div>
    </>
  );
};
